import type { ChipProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";

export type SeverityLevel = "info" | "warning" | "error" | "fatal";

export function normalizeSeverityLevel(
  severity: string | number | null | undefined
): SeverityLevel | null {
  const key = String(severity ?? "")
    .trim()
    .toLowerCase();
  if (key === "info" || key === "warning" || key === "error" || key === "fatal") {
    return key;
  }
  return null;
}

/** MUI Chip props for catalog severity (Info, Warning, Error, Fatal). */
export function severityChipProps(
  severity: string | number | null | undefined
): Pick<ChipProps, "label" | "color" | "variant" | "sx"> {
  const label = String(severity ?? "").trim() || "—";
  const level = normalizeSeverityLevel(severity);

  switch (level) {
    case "info":
      return { label, color: "info", variant: "outlined" };
    case "warning":
      return { label, color: "warning", variant: "outlined" };
    case "error":
      return { label, color: "error", variant: "outlined" };
    case "fatal":
      return {
        label,
        color: "error",
        variant: "filled",
        sx: { fontWeight: 700 },
      };
    default:
      return { label, variant: "outlined" };
  }
}

/** Theme accent (border, icons) aligned with severity chip palette. */
export function severityAccentColor(
  theme: Theme,
  severity: string | number | null | undefined
): string {
  const level = normalizeSeverityLevel(severity);
  switch (level) {
    case "info":
      return theme.palette.info.main;
    case "warning":
      return theme.palette.warning.main;
    case "error":
      return theme.palette.error.main;
    case "fatal":
      return theme.palette.error.dark;
    default:
      return theme.palette.text.secondary;
  }
}
